"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScoFiscalOpenHoldListView = exports.ScoFiscalOpenHoldView = exports.ScoFiscalHoldView = exports.ScoFiscalHoldCommandView = exports.IssueScoFiscalHoldCommand = exports.ScoFiscalHoldReportResponse = exports.ScoFiscalHoldCommand = exports.ScoFiscalHoldReport = exports.ScoFiscalHoldCommandResult = exports.ScoFiscalHoldActor = exports.ScoFiscalHold = exports.ScoFiscalHoldOutcome = exports.ScoFiscalHoldCommandKind = exports.ScoFiscalHoldCardState = exports.ScoFiscalHoldState = exports.ScoFiscalHoldPhase = exports.SCO_FISCAL_HOLD_NOT_BEGUN_ALERT_MS = exports.SCO_FISCAL_HOLD_NOT_BEGUN_ALERT_MINUTES = exports.SCO_FISCAL_HOLD_REPORT_FRESH_MS = exports.SCO_FISCAL_HOLD_COMMAND_TTL_MS = exports.SCO_FISCAL_HOLD_RESOLVE_PERMISSION = exports.SCO_FISCAL_HOLD_AUTO_RETRIES = exports.SCO_FISCAL_HOLD_MAX_RESULTS = exports.SCO_FISCAL_HOLD_MAX_HOLDS = void 0;
const zod_1 = require("zod");
/**
 * EAT-23 (EAT-16 slice P0) — Back Office resolves a sale held for fiscal
 * recovery on an unattended self-checkout lane.
 *
 * When a paid sale cannot be fiscalised, the lane holds it (RFL-271 fiscal
 * recovery: `commit_unknown`, `fiscalisation_pending`, `identity_pending`,
 * `completion_pending`) and blocks the next customer until the hold is
 * resolved. The lane retries on its own; when that fails, a person decides. On
 * an unattended store nobody is at the lane, so this channel lets Back Office
 * order the lane to retry once more or to void the sale.
 *
 * Decisions (EAT-16):
 * - one person resolves remotely, behind a separate permission
 *   (`sco-fiscal-hold.resolve`); audited, no second approver;
 * - Back Office alerts only after the lane's own automatic retries
 *   ({@link SCO_FISCAL_HOLD_AUTO_RETRIES}) have failed, which the lane states
 *   as `autoRetriesExhausted`;
 * - a remote void may reverse an already-captured card leg, through the card
 *   terminal (EPS) from the lane; it never captures a second time;
 * - a remote retry does not reset the lane's automatic-retry budget: after it
 *   the hold stays `autoRetriesExhausted` and `retryCount` keeps counting.
 *
 * The channel copies MICRO-46's card end of day (`sco-card-eod.ts`): the lane
 * reports (its holds, results of earlier commands) on
 * `POST /sync/sco/fiscal-holds`, and the answer carries the commands addressed
 * to that lane. A command is handed out on every report until its result
 * arrives; the lane applies a `commandId` once and answers a repeat with the
 * first outcome.
 *
 * Only a hold carries an amount — the sale total, so an operator can tell the
 * sale — as integer minor units. Nothing carries a card number or free text:
 * errors and outcomes are stable codes.
 */
/** Most holds one report may carry, and most results / commands per message. */
exports.SCO_FISCAL_HOLD_MAX_HOLDS = 50;
exports.SCO_FISCAL_HOLD_MAX_RESULTS = 50;
/** The lane's automatic retries before Back Office is alerted (EAT-16). */
exports.SCO_FISCAL_HOLD_AUTO_RETRIES = 3;
/** The permission that resolves a hold remotely (one person, audited). */
exports.SCO_FISCAL_HOLD_RESOLVE_PERMISSION = 'sco-fiscal-hold.resolve';
/** How long a command waits to be collected before it expires (as MICRO-46). */
exports.SCO_FISCAL_HOLD_COMMAND_TTL_MS = 24 * 60 * 60 * 1000;
/** How recent a lane's report must be for Back Office to act on it (as MICRO-46). */
exports.SCO_FISCAL_HOLD_REPORT_FRESH_MS = 15 * 60 * 1000;
/**
 * EAT-25 (MICRO-57 follow-up): how long a held sale may keep being refused by
 * the fiscal device before its receipt begins (`notBegunSince`) before Back
 * Office alerts, as it does on `autoRetriesExhausted`. Such refusals spend no
 * automatic retry, so without this a lane whose fiscal device stays down
 * would never page anyone. 10 minutes, confirmed by Igoris (2026-10-29).
 * BOS decides the alert from this constant only; the rule's summary
 * (`infra/loki/rules/platform/lane-pipeline.yml`) and the runbook state the
 * 10 minutes in words, so change them with it.
 */
exports.SCO_FISCAL_HOLD_NOT_BEGUN_ALERT_MINUTES = 10;
exports.SCO_FISCAL_HOLD_NOT_BEGUN_ALERT_MS = exports.SCO_FISCAL_HOLD_NOT_BEGUN_ALERT_MINUTES * 60 * 1000;
/** A stable code (`fiscal_device_timeout`); never a message. */
const Code = zod_1.z
    .string()
    .max(64)
    .regex(/^[a-z][a-z0-9_.-]*$/, 'a stable code, not free text');
/** The lane's journal id of the held sale. */
const JournalId = zod_1.z.string().min(1).max(64);
/**
 * The hold's recovery phase, as the lane's fiscal recovery store writes it
 * (terminals `FiscalRecoveryPhase`):
 * - `commit_unknown`: the document may or may not have committed; never
 *   re-registered blindly;
 * - `fiscalisation_pending`: no document committed; a retry registers the same
 *   frozen sale again;
 * - `identity_pending`: the document committed, its number was not read back; a
 *   retry only reads it back;
 * - `completion_pending`: fiscalised; only the upload / receipt is unfinished.
 */
exports.ScoFiscalHoldPhase = zod_1.z.enum([
    'commit_unknown',
    'fiscalisation_pending',
    'identity_pending',
    'completion_pending',
]);
/**
 * The sale's card leg: `none` (no card tender), `authorised` (held on the card,
 * not captured), `captured`, or `unknown` (the lane cannot tell, e.g. the card
 * terminal's answer was lost).
 */
/**
 * Where the held sale stands on the lane.
 * - `held`: the sale waits for fiscal recovery in `phase`;
 * - `void_card_reversal_pending`: the sale is voided, but its captured (or
 *   possibly captured) card leg still awaits its reversal on the card terminal
 *   (the lane's `FiscalRecoveryCardReversalPending`). The lane stays blocked
 *   and keeps reporting the sale in this state, with the phase it was voided
 *   in, until the reversal is confirmed; then it drops out of the report. No
 *   command acts on it: the lane answers one naming it `phase_changed`.
 */
exports.ScoFiscalHoldState = zod_1.z.enum(['held', 'void_card_reversal_pending']);
exports.ScoFiscalHoldCardState = zod_1.z.enum(['none', 'authorised', 'captured', 'unknown']);
exports.ScoFiscalHoldCommandKind = zod_1.z.enum(['retry', 'void']);
/**
 * What became of a command.
 * - `applied_completed`: a retry fiscalised and finished the sale;
 * - `retry_failed`: a retry was attempted and failed; the hold stays and the next
 *   report's `lastErrorCode` says why;
 * - `applied_voided`: the sale is voided; an authorised card leg was released
 *   (or there was none);
 * - `applied_voided_card_reversed`: the sale is voided and its captured card leg
 *   was reversed through the card terminal (never a second capture);
 * - `card_release_failed` / `card_reversal_failed`: the sale is voided but its card
 *   leg could not be released / reversed; the lane keeps the obligation, stays
 *   blocked and reports the sale as `void_card_reversal_pending`;
 * - `not_found`: no hold with that journal id;
 * - `phase_changed`: the hold is no longer in `expectedPhase`; nothing done;
 * - `not_voidable`: a void on a hold the lane may not void (its document may be
 *   committed); nothing done;
 * - `busy`: the lane was in the middle of something (a retry in flight);
 *   nothing done;
 * - `device_unavailable`: nothing was attempted because the fiscal device (or, for
 *   a void, the card terminal) is down; the hold stays;
 * - `expired`: past `expiresAt` — set by Back Office for a command never
 *   collected, or by the lane for one it received too late; nothing done.
 *
 * Every outcome is final for its `commandId`: to try again, issue a new command.
 */
exports.ScoFiscalHoldOutcome = zod_1.z.enum([
    'applied_completed',
    'retry_failed',
    'applied_voided',
    'applied_voided_card_reversed',
    'card_release_failed',
    'card_reversal_failed',
    'not_found',
    'phase_changed',
    'not_voidable',
    'busy',
    'device_unavailable',
    'expired',
]);
/** One held sale as the lane reports it. */
exports.ScoFiscalHold = zod_1.z.object({
    journalId: JournalId,
    state: exports.ScoFiscalHoldState,
    /** The recovery phase; for `void_card_reversal_pending`, the one it was voided in. */
    phase: exports.ScoFiscalHoldPhase,
    /** When the sale was first held. */
    since: zod_1.z.string().datetime(),
    /** The sale total in integer minor units; never a float. */
    totalMinor: zod_1.z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    currency: zod_1.z.string().regex(/^[A-Z]{3}$/, 'an ISO 4217 code'),
    cardState: exports.ScoFiscalHoldCardState,
    /** Every retry so far, automatic and remote; a remote retry adds to it. */
    retryCount: zod_1.z.number().int().min(0),
    /** Whether the lane may still retry this hold on its own (durable, phase allows). */
    canAutoRetry: zod_1.z.boolean(),
    /** The lane's automatic retries have all failed: Back Office alerts. */
    autoRetriesExhausted: zod_1.z.boolean(),
    /**
     * Whether the lane would accept a `void` now: the lane's
     * `fiscalRecoveryCanResolveVoided` for the hold, and no retry in flight.
     * Always false for `void_card_reversal_pending`.
     */
    voidable: zod_1.z.boolean(),
    lastErrorCode: Code.nullable(),
    /**
     * EAT-25 / EAT-27 (MICRO-57 follow-up): since when the fiscal device has
     * refused this sale BEFORE its receipt began (LT SM unreachable, `rb`
     * refused) — the first refusal of the current streak, by the lane's clock.
     * Such a refusal spends none of the automatic retries, so a lane whose
     * device stays down never reports `autoRetriesExhausted`; Back Office alerts
     * on this age instead ({@link SCO_FISCAL_HOLD_NOT_BEGUN_ALERT_MS}). Cleared
     * on the lane when the device accepts the sale, the receipt begins or the
     * hold resolves; a later refusal starts a new streak.
     *
     * Optional, as is `notBegunCount`: an older lane omits both, and Back Office
     * reads that as "never refused before begin". The lane omits it with no
     * streak, never sends null.
     */
    notBegunSince: zod_1.z.string().datetime().optional(),
    /** How many refusals before begin the current streak has had; omitted when 0. */
    notBegunCount: zod_1.z.number().int().min(0).optional(),
});
/**
 * The Back Office user who issued a command, as on MICRO-46's card end of day:
 * an opaque user id and the display name, which the lane's journal records as
 * who resolved the hold. Nothing else about the person.
 */
exports.ScoFiscalHoldActor = zod_1.z.object({
    userId: zod_1.z.string(),
    name: zod_1.z.string().min(1).max(128),
});
/** What the lane did with one command. */
exports.ScoFiscalHoldCommandResult = zod_1.z.object({
    commandId: zod_1.z.string().uuid(),
    outcome: exports.ScoFiscalHoldOutcome,
    appliedAt: zod_1.z.string().datetime(),
});
/** `POST /sync/sco/fiscal-holds`: one lane's holds and command results. */
exports.ScoFiscalHoldReport = zod_1.z.object({
    /** The reporting lane; Back Office refuses one that is not the token's terminal. */
    terminalId: zod_1.z.string().min(1).max(64),
    reportedAt: zod_1.z.string().datetime(),
    /** Every hold the lane has now; empty when none. */
    holds: zod_1.z.array(exports.ScoFiscalHold).max(exports.SCO_FISCAL_HOLD_MAX_HOLDS),
    results: zod_1.z.array(exports.ScoFiscalHoldCommandResult).max(exports.SCO_FISCAL_HOLD_MAX_RESULTS),
});
/** One command, as the lane receives it. */
exports.ScoFiscalHoldCommand = zod_1.z.object({
    commandId: zod_1.z.string().uuid(),
    kind: exports.ScoFiscalHoldCommandKind,
    holdJournalId: JournalId,
    /** The lane refuses (`phase_changed`) when the hold has moved on. */
    expectedPhase: exports.ScoFiscalHoldPhase,
    actor: exports.ScoFiscalHoldActor,
    issuedAt: zod_1.z.string().datetime(),
    expiresAt: zod_1.z.string().datetime(),
});
/** The answer to `POST /sync/sco/fiscal-holds`: the commands for that lane. */
exports.ScoFiscalHoldReportResponse = zod_1.z.object({
    commands: zod_1.z.array(exports.ScoFiscalHoldCommand).max(exports.SCO_FISCAL_HOLD_MAX_RESULTS),
});
/**
 * `POST /sco/fiscal-holds/commands`: an operator's retry or void of one hold.
 *
 * EAT-25 review: a void may reverse a card payment, so it is bound to what the
 * operator saw and acknowledged, on Back Office and not only in the admin UI.
 * A void names `expectedCardState`, the card state shown when it was
 * confirmed (refused `card_state_changed` when the lane reports another), and
 * `cardAcknowledged` must be true for a `captured` or `unknown` card payment
 * (refused `acknowledgement_required`). A retry stays bound to the phase only.
 * Neither field reaches the lane: the command it receives is unchanged.
 */
exports.IssueScoFiscalHoldCommand = zod_1.z
    .object({
    terminalId: zod_1.z.string().min(1).max(64),
    holdJournalId: JournalId,
    kind: exports.ScoFiscalHoldCommandKind,
    expectedPhase: exports.ScoFiscalHoldPhase,
    expectedCardState: exports.ScoFiscalHoldCardState.optional(),
    cardAcknowledged: zod_1.z.boolean().optional(),
})
    .superRefine((value, ctx) => {
    if (value.kind === 'void' && value.expectedCardState === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['expectedCardState'],
            message: 'a void names the card state it was decided on',
        });
    }
});
/** A command as an operator sees it. */
exports.ScoFiscalHoldCommandView = exports.ScoFiscalHoldCommand.extend({
    deliveredAt: zod_1.z.string().datetime().nullable(),
    completedAt: zod_1.z.string().datetime().nullable(),
    outcome: exports.ScoFiscalHoldOutcome.nullable(),
});
/** `GET /sco/fiscal-holds?terminalId=`: a lane's holds and their commands. */
exports.ScoFiscalHoldView = zod_1.z.object({
    terminalId: zod_1.z.string(),
    /** The lane's last report; null when it never reported. */
    reportedAt: zod_1.z.string().datetime().nullable(),
    /** Whether the report is recent enough to act on. */
    fresh: zod_1.z.boolean(),
    /**
     * EAT-25: how old the lane's last report is by Back Office's clock, in
     * milliseconds; null when it never reported. Optional: absent from a Back
     * Office that predates it.
     */
    reportAgeMs: zod_1.z.number().int().min(0).nullable().optional(),
    holds: zod_1.z.array(exports.ScoFiscalHold.extend({
        commands: zod_1.z.array(exports.ScoFiscalHoldCommandView),
    })),
});
/**
 * EAT-25 (EAT-16 slice P2): one open hold on any lane of the tenant, with the
 * lane and its store, for the cross-store list (`GET /sco/fiscal-holds/open`).
 */
exports.ScoFiscalOpenHoldView = exports.ScoFiscalHold.extend({
    terminalId: zod_1.z.string(),
    terminalCode: zod_1.z.string(),
    terminalName: zod_1.z.string(),
    /** The lane's store; null for a lane not assigned to one. */
    locationId: zod_1.z.string().nullable(),
    locationName: zod_1.z.string().nullable(),
    /** When Back Office first saw this hold. */
    firstReportedAt: zod_1.z.string().datetime(),
    /** How old the lane's last report is (Back Office clock); null when none. */
    reportAgeMs: zod_1.z.number().int().min(0).nullable(),
    /** No report in {@link SCO_FISCAL_HOLD_REPORT_FRESH_MS}: not to act on. */
    stale: zod_1.z.boolean(),
});
/** `GET /sco/fiscal-holds/open`: every open hold of the tenant, oldest first. */
exports.ScoFiscalOpenHoldListView = zod_1.z.object({
    holds: zod_1.z.array(exports.ScoFiscalOpenHoldView),
});
//# sourceMappingURL=sco-fiscal-hold.js.map